import { Box, Checkbox } from "@chakra-ui/react";
import { Trans } from "@lingui/macro";
import { useUpdateTimeTrackingSettingsForProjectMutation } from "@src/__generated__/graphql";
import { onError } from "@src/utils/apollo";
import { toApiDate } from "@src/utils/dates";
import { formatDate } from "@src/utils/formatters";
import { FieldState, FormState } from "formstate";
import { map } from "lodash";
import { observer, useLocalObservable } from "mobx-react-lite";
import type React from "react";
import { type FunctionComponent, useContext, useEffect, useState } from "react";
import type { OurWorkItemModel } from "../models";
import { PageProjectSettingsCtx } from "../PageProjectSettings";
import { DateLimitationPicker } from "./DateLimitationPicker";
import {
  StoreDateLimitation,
  StoreDateLimitationCtx,
} from "./StoreDateLimitation";

class Form {
  fields = {
    tracking_enabled: new FieldState(false).validators(),
    tracking_enabled_from: new FieldState<Date | undefined>(
      undefined,
    ).validators(),
    tracking_enabled_to: new FieldState<Date | undefined>(
      undefined,
    ).validators(),
  };
  form = new FormState(this.fields).enableAutoValidation();
}

export const TableTimeTracking = observer(function TableTimeTracking() {
  const store = useContext(PageProjectSettingsCtx);

  return (
    <div>
      <div className="flex flex-row bg-lightGray border-b border-skyDark py-4 px-6">
        <span className="w-1/3">
          <Trans>Work budget item</Trans>
        </span>
        <span className="w-1/4">
          <Trans>Tracking enabled</Trans>
        </span>
        <span className="flex-1">
          <Trans>Date limitation</Trans>
        </span>
      </div>
      <div>
        {map(store.projectSettings?.ourWork, (w) => (
          <Row row={w}></Row>
        ))}
        {!store.projectSettings?.ourWork.length && (
          <div className="p-6 flex justify-center">
            <Trans>This project does not have any budget for our work</Trans>
          </div>
        )}
      </div>
    </div>
  );
});

const Row: FunctionComponent<
  React.PropsWithChildren<{ row: OurWorkItemModel }>
> = observer(function Row({ row }) {
  const store = useContext(PageProjectSettingsCtx);

  const [storeDateLimitation] = useState(() => {
    const { tracking_enabled_from, tracking_enabled_to, tracking_enabled } =
      row.timeTrackingSettings;

    const store = StoreDateLimitation.create();

    store.selectedDates = {
      start: tracking_enabled_from,
      end: tracking_enabled_to,
    };

    store.enabled = tracking_enabled;
    store.inputFromValue = tracking_enabled_from
      ? formatDate(tracking_enabled_from)
      : "";
    store.inputToValue = tracking_enabled_to
      ? formatDate(tracking_enabled_to)
      : "";

    return store;
  });

  const state = useLocalObservable(() => ({
    form: new Form(),
  }));

  const {
    form: { fields },
  } = state;

  useEffect(() => {
    fields.tracking_enabled.onChange(row.timeTrackingSettings.tracking_enabled);
    fields.tracking_enabled_from.onChange(
      row.timeTrackingSettings.tracking_enabled_from,
    );
    fields.tracking_enabled_to.onChange(
      row.timeTrackingSettings.tracking_enabled_to,
    );

    fields.tracking_enabled.onDidChange(({ newValue }) => {
      storeDateLimitation.enabled = newValue;
      if (!newValue) {
        fields.tracking_enabled_from.onChange(undefined);
        fields.tracking_enabled_to.onChange(undefined);
      }
      doUpdateSettings();
    });
  }, []);

  const [_updateSettings] = useUpdateTimeTrackingSettingsForProjectMutation({
    ...onError(),
  });

  function doUpdateSettings() {
    _updateSettings({
      variables: {
        input: {
          project_id: store.projectSettings?.id!,
          our_work_budget_items: [
            {
              our_work_budget_item_id: row.id,
              tracking_enabled: fields.tracking_enabled.value,
              tracking_enabled_from: fields.tracking_enabled_from.value
                ? toApiDate(fields.tracking_enabled_from.value)
                : null,
              tracking_enabled_to: fields.tracking_enabled_to.value
                ? toApiDate(fields.tracking_enabled_to.value)
                : null,
            },
          ],
        },
      },
    });
  }

  useEffect(() => {
    fields.tracking_enabled_from.onChange(
      storeDateLimitation.selectedDates.start,
    );
    fields.tracking_enabled_to.onChange(storeDateLimitation.selectedDates.end);
  }, [storeDateLimitation.selectedDates]);

  return (
    <StoreDateLimitationCtx.Provider value={storeDateLimitation}>
      <div className="border-b border-sky p-6 flex flex-row items-center">
        <Box w="33%" maxW="33%" wordBreak="keep-all">
          {row.title} - {row.categoryName}
        </Box>
        <div className="w-1/4">
          <Checkbox
            isChecked={fields.tracking_enabled.value}
            onChange={(event) => {
              fields.tracking_enabled.onChange(event.target.checked);
            }}
          />
        </div>
        <div className="flex-1">
          <DateLimitationPicker onSubmit={doUpdateSettings} />
        </div>
      </div>
    </StoreDateLimitationCtx.Provider>
  );
});
